import { normalizeIpId } from '$/domains/myIp/myIpService';
import type { CanvasPublishIpCandidatesResult } from './type';

/** 发布读取与写入都只接受对象，避免错误响应被当成可提交的空配置。 */
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid publish data');
  return value as Record<string, unknown>;
}

/** 兼容接口的小整数和无损解析后的长字符串，拒绝已经发生舍入的 number。 */
export function normalizePublishIpIds(value: unknown): string[] {
  if (!Array.isArray(value)) throw new Error('Invalid publish IP selection');
  return value.map(normalizeIpId);
}

/** 保留未下发、null 和显式空数组的不同含义，只规范化实际下发的 IP 选择。 */
export function normalizePublishIpSelection(value: unknown): Record<string, unknown> {
  const data = record(value);
  const normalized = { ...data };
  (['selected_ip_ids', 'ip_ids'] as const).forEach((field) => {
    if (data[field] !== undefined && data[field] !== null) normalized[field] = normalizePublishIpIds(data[field]);
  });
  return normalized;
}

/** 只改变候选身份的表示，保留服务端排序、默认选择和其他展示字段。 */
export function normalizePublishIpCandidates(value: unknown): CanvasPublishIpCandidatesResult {
  const data = record(value);
  if (data.list === undefined || data.list === null) return data as unknown as CanvasPublishIpCandidatesResult;
  if (!Array.isArray(data.list)) throw new Error('Invalid publish IP candidates');
  return {
    ...data,
    list: data.list.map((entry) => {
      const candidate = record(entry);
      return { ...candidate, ip_id: normalizeIpId(candidate.ip_id) };
    }),
  } as CanvasPublishIpCandidatesResult;
}

/** 发布列表也会直接进入编辑弹窗，不能只在单条 info 入口规范化身份。 */
export function normalizePublishIpList(value: unknown): Record<string, unknown> {
  const data = record(value);
  if (data.list === undefined || data.list === null) return data;
  if (!Array.isArray(data.list)) throw new Error('Invalid publish list');
  return { ...data, list: data.list.map(normalizePublishIpSelection) };
}

/** 拦截器补齐公共参数后再序列化；仅将已验证的 uint64 数组输出为 JSON 数字。 */
export function serializePublishIpRequest(value: unknown): string {
  const data = record(value);
  if (data.ip_ids === undefined) return JSON.stringify(data);
  const ids = normalizePublishIpIds(data.ip_ids);
  const rest = { ...data };
  delete rest.ip_ids;
  const body = JSON.stringify(rest);
  // 按顶层字段拼接，避免用字符串替换误改标题或描述中恰好相同的文本。
  return `${body.slice(0, -1)}${body === '{}' ? '' : ','}"ip_ids":[${ids.join(',')}]}`;
}
